import React, { useState, useEffect, useRef } from 'react';
import { 
  FiFileText, FiPlus, FiSearch, FiFilter, FiExternalLink,
  FiEdit2, FiTrash2, FiDownload, FiCheck, FiX, FiUpload,
  FiBookOpen, FiGlobe, FiDatabase, FiAward
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Jurnal.css';

const Jurnal = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('my-journals');
  const [jurnals, setJurnals] = useState([]);
  const [stats, setStats] = useState({ total: 0, published: 0, draft: 0, claimed: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSumber, setFilterSumber] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedJurnal, setSelectedJurnal] = useState(null);
  
  // Claim search states
  const [claimSearchQuery, setClaimSearchQuery] = useState('');
  const [claimSumber, setClaimSumber] = useState('internal'); // Default to internal
  const [claimResults, setClaimResults] = useState([]);
  const [claimSearching, setClaimSearching] = useState(false);
  
  // Upload form states
  const [uploadForm, setUploadForm] = useState({
    judul: '',
    penulis: '',
    abstrak: '',
    tahun_terbit: new Date().getFullYear(),
    nama_jurnal: '',
    volume: '',
    nomor: '',
    halaman: '',
    issn: '',
    doi: '',
    url_sumber: '',
    status: 'draft'
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user?.id) {
      fetchJurnals();
    }
  }, [user, searchQuery, filterSumber, filterStatus]);

  const fetchJurnals = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (filterSumber) params.sumber = filterSumber;
      if (filterStatus) params.status = filterStatus;
      
      const response = await api.getJurnals(user.id, params);
      if (response.success) {
        setJurnals(response.data || []);
        setStats(response.stats || { total: 0, published: 0, draft: 0, claimed: 0 });
      }
    } catch (error) {
      console.error('Error fetching journals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('user_id', user.id);
      Object.keys(uploadForm).forEach(key => {
        formData.append(key, uploadForm[key]);
      });
      if (uploadFile) {
        formData.append('file', uploadFile);
      }
      formData.append('sumber', 'upload');
      
      const response = await api.createJurnal(formData);
      if (response.success) {
        setMessage({ type: 'success', text: 'Jurnal berhasil ditambahkan!' });
        setShowUploadModal(false);
        resetUploadForm();
        fetchJurnals();
      } else {
        setMessage({ type: 'error', text: response.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Gagal menambahkan jurnal' });
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadForm({
      judul: '',
      penulis: '',
      abstrak: '',
      tahun_terbit: new Date().getFullYear(),
      nama_jurnal: '',
      volume: '',
      nomor: '',
      halaman: '',
      issn: '',
      doi: '',
      url_sumber: '',
      status: 'draft'
    });
    setUploadFile(null);
  };

  const handleClaimSearch = async () => {
    if (!claimSearchQuery.trim() && claimSumber !== 'internal') return;
    
    setClaimSearching(true);
    try {
      let response;
      
      if (claimSumber === 'internal') {
        // Search from internal database (admin-uploaded journals)
        response = await api.searchInternalJurnals(user?.nama, claimSearchQuery);
      } else {
        // Search from external sources
        response = await api.searchExternalJurnals(claimSearchQuery, claimSumber, user.id);
      }
      
      if (response.success) {
        setClaimResults(response.data || []);
      }
    } catch (error) {
      console.error('Error searching journals:', error);
    } finally {
      setClaimSearching(false);
    }
  };

  // Auto-search internal journals when modal opens
  useEffect(() => {
    if (showClaimModal && claimSumber === 'internal' && user?.nama) {
      handleClaimSearch();
    }
  }, [showClaimModal]);

  const handleClaimJurnal = async (jurnal) => {
    try {
      let response;
      
      if (jurnal.id_jurnal) {
        // Claim internal journal
        response = await api.claimInternalJurnal(user.id, jurnal.id_jurnal);
      } else {
        // Claim external journal
        response = await api.claimJurnal({
          user_id: user.id,
          ...jurnal
        });
      }
      
      if (response.success) {
        setMessage({ type: 'success', text: 'Jurnal berhasil diklaim!' });
        // Update claimed status in results
        setClaimResults(prev => prev.map(j => 
          (j.id_jurnal === jurnal.id_jurnal || j.doi === jurnal.doi) ? { ...j, is_claimed: true } : j
        ));
        fetchJurnals();
      } else {
        setMessage({ type: 'error', text: response.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Gagal klaim jurnal' });
    }
  };

  const handleDeleteJurnal = async (jurnalId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus jurnal ini?')) return;
    
    try {
      const response = await api.deleteJurnal(user.id, jurnalId);
      if (response.success) {
        setMessage({ type: 'success', text: 'Jurnal berhasil dihapus' });
        fetchJurnals();
      } else {
        setMessage({ type: 'error', text: response.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Gagal menghapus jurnal' });
    }
  };

  const getSumberIcon = (sumber) => {
    switch (sumber) {
      case 'google_scholar': return <FiGlobe />;
      case 'ebsco': return <FiDatabase />;
      case 'scopus': return <FiAward />;
      case 'doaj': return <FiBookOpen />;
      case 'crossref': return <FiFileText />;
      default: return <FiUpload />;
    }
  };

  const getSumberLabel = (sumber) => {
    const labels = {
      'upload': 'Upload',
      'Manual': 'Manual',
      'internal': 'Database Perpustakaan',
      'google_scholar': 'Google Scholar',
      'ebsco': 'EBSCO',
      'doaj': 'DOAJ',
      'crossref': 'CrossRef',
      'scopus': 'Scopus',
      'lainnya': 'Lainnya'
    };
    return labels[sumber] || sumber || 'Manual';
  };

  const getStatusBadge = (status) => {
    const badges = {
      'draft': { class: 'draft', label: 'Draft' },
      'published': { class: 'published', label: 'Published' },
      'claimed': { class: 'claimed', label: 'Diklaim' },
      'verified': { class: 'verified', label: 'Terverifikasi' }
    };
    const badge = badges[status] || badges.draft;
    return <span className={`jurnal__status jurnal__status--${badge.class}`}>{badge.label}</span>;
  };

  return (
    <div className="jurnal">
      {/* Message */}
      {message.text && (
        <div className={`jurnal__message jurnal__message--${message.type}`}>
          {message.type === 'success' ? <FiCheck /> : <FiX />}
          <span>{message.text}</span>
          <button onClick={() => setMessage({ type: '', text: '' })}><FiX /></button>
        </div>
      )}

      {/* Header */}
      <div className="jurnal__header">
        <div className="jurnal__header-left">
          <h1><FiFileText /> Jurnal Saya</h1>
          <p>Kelola publikasi jurnal ilmiah Anda</p>
        </div>
        <div className="jurnal__header-actions">
          <button className="jurnal__btn jurnal__btn--secondary" onClick={() => setShowClaimModal(true)}>
            <FiSearch /> Klaim Jurnal
          </button>
          <button className="jurnal__btn jurnal__btn--primary" onClick={() => setShowUploadModal(true)}>
            <FiPlus /> Upload Jurnal
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="jurnal__stats">
        <div className="jurnal__stat">
          <span className="jurnal__stat-value">{stats.total || 0}</span>
          <span className="jurnal__stat-label">Total Jurnal</span>
        </div>
        <div className="jurnal__stat jurnal__stat--published">
          <span className="jurnal__stat-value">{stats.published || 0}</span>
          <span className="jurnal__stat-label">Published</span>
        </div>
        <div className="jurnal__stat jurnal__stat--claimed">
          <span className="jurnal__stat-value">{stats.claimed || 0}</span>
          <span className="jurnal__stat-label">Diklaim</span>
        </div>
        <div className="jurnal__stat jurnal__stat--draft">
          <span className="jurnal__stat-value">{stats.draft || 0}</span>
          <span className="jurnal__stat-label">Draft</span>
        </div>
      </div>

      {/* Filters */}
      <div className="jurnal__filters">
        <div className="jurnal__search">
          <FiSearch />
          <input
            type="text"
            placeholder="Cari judul, penulis, atau jurnal..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select 
          value={filterSumber} 
          onChange={(e) => setFilterSumber(e.target.value)}
          className="jurnal__filter-select"
        >
          <option value="">Semua Sumber</option>
          <option value="upload">Upload</option>
          <option value="google_scholar">Google Scholar</option>
          <option value="ebsco">EBSCO</option>
          <option value="doaj">DOAJ</option>
          <option value="crossref">CrossRef</option>
          <option value="scopus">Scopus</option>
        </select>
        <select 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
          className="jurnal__filter-select"
        >
          <option value="">Semua Status</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="claimed">Diklaim</option>
          <option value="verified">Terverifikasi</option>
        </select>
      </div>

      {/* Journal List */}
      <div className="jurnal__list">
        {loading ? (
          <div className="jurnal__loading">
            <div className="jurnal__loading-spinner"></div>
            <p>Memuat data jurnal...</p>
          </div>
        ) : jurnals.length === 0 ? (
          <div className="jurnal__empty">
            <FiFileText />
            <h3>Belum ada jurnal</h3>
            <p>Upload jurnal baru atau klaim jurnal dari database eksternal</p>
          </div>
        ) : (
          jurnals.map(jurnal => (
            <div key={jurnal.id_jurnal} className="jurnal__item">
              <div className="jurnal__item-header">
                <div className="jurnal__item-sumber">
                  {getSumberIcon(jurnal.sumber)}
                  <span>{getSumberLabel(jurnal.sumber)}</span>
                </div>
                {getStatusBadge(jurnal.status)}
              </div>
              <h3 className="jurnal__item-title">{jurnal.judul}</h3>
              <p className="jurnal__item-authors">{jurnal.penulis}</p>
              <div className="jurnal__item-meta">
                {jurnal.nama_jurnal && <span>{jurnal.nama_jurnal}</span>}
                {jurnal.tahun_terbit && <span>{jurnal.tahun_terbit}</span>}
                {jurnal.volume && <span>Vol. {jurnal.volume}</span>}
                {jurnal.nomor && <span>No. {jurnal.nomor}</span>}
              </div>
              {jurnal.doi && (
                <p className="jurnal__item-doi">DOI: {jurnal.doi}</p>
              )}
              <div className="jurnal__item-actions">
                <button 
                  className="jurnal__item-btn"
                  onClick={() => { setSelectedJurnal(jurnal); setShowDetailModal(true); }}
                >
                  <FiExternalLink /> Detail
                </button>
                {jurnal.file_path && (
                  <a 
                    href={`/${jurnal.file_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="jurnal__item-btn"
                  >
                    <FiDownload /> Download
                  </a>
                )}
                {jurnal.url_sumber && (
                  <a 
                    href={jurnal.url_sumber}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="jurnal__item-btn"
                  >
                    <FiGlobe /> Sumber
                  </a>
                )}
                <button 
                  className="jurnal__item-btn jurnal__item-btn--danger"
                  onClick={() => handleDeleteJurnal(jurnal.id_jurnal)}
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="jurnal__modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="jurnal__modal" onClick={e => e.stopPropagation()}>
            <div className="jurnal__modal-header">
              <h2><FiUpload /> Upload Jurnal Baru</h2>
              <button onClick={() => setShowUploadModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleUploadSubmit} className="jurnal__modal-body">
              <div className="jurnal__form-row">
                <div className="jurnal__form-group jurnal__form-group--full">
                  <label>Judul Jurnal *</label>
                  <input
                    type="text"
                    value={uploadForm.judul}
                    onChange={e => setUploadForm({ ...uploadForm, judul: e.target.value })}
                    required
                    placeholder="Masukkan judul lengkap jurnal"
                  />
                </div>
              </div>
              <div className="jurnal__form-row">
                <div className="jurnal__form-group jurnal__form-group--full">
                  <label>Penulis *</label>
                  <input
                    type="text"
                    value={uploadForm.penulis}
                    onChange={e => setUploadForm({ ...uploadForm, penulis: e.target.value })}
                    required
                    placeholder="Nama penulis (pisahkan dengan koma)"
                  />
                </div>
              </div>
              <div className="jurnal__form-row">
                <div className="jurnal__form-group jurnal__form-group--full">
                  <label>Abstrak</label>
                  <textarea
                    value={uploadForm.abstrak}
                    onChange={e => setUploadForm({ ...uploadForm, abstrak: e.target.value })}
                    rows={4}
                    placeholder="Abstrak jurnal..."
                  />
                </div>
              </div>
              <div className="jurnal__form-row">
                <div className="jurnal__form-group">
                  <label>Nama Jurnal</label>
                  <input
                    type="text"
                    value={uploadForm.nama_jurnal}
                    onChange={e => setUploadForm({ ...uploadForm, nama_jurnal: e.target.value })}
                    placeholder="Nama jurnal/publikasi"
                  />
                </div>
                <div className="jurnal__form-group">
                  <label>Tahun Terbit</label>
                  <input
                    type="number"
                    value={uploadForm.tahun_terbit}
                    onChange={e => setUploadForm({ ...uploadForm, tahun_terbit: e.target.value })}
                    min="1900"
                    max={new Date().getFullYear()}
                  />
                </div>
              </div>
              <div className="jurnal__form-row">
                <div className="jurnal__form-group">
                  <label>Volume</label>
                  <input
                    type="text"
                    value={uploadForm.volume}
                    onChange={e => setUploadForm({ ...uploadForm, volume: e.target.value })}
                  />
                </div>
                <div className="jurnal__form-group">
                  <label>Nomor</label>
                  <input
                    type="text"
                    value={uploadForm.nomor}
                    onChange={e => setUploadForm({ ...uploadForm, nomor: e.target.value })}
                  />
                </div>
                <div className="jurnal__form-group">
                  <label>Halaman</label>
                  <input
                    type="text"
                    value={uploadForm.halaman}
                    onChange={e => setUploadForm({ ...uploadForm, halaman: e.target.value })}
                    placeholder="123-145"
                  />
                </div>
              </div>
              <div className="jurnal__form-row">
                <div className="jurnal__form-group">
                  <label>ISSN</label>
                  <input
                    type="text"
                    value={uploadForm.issn}
                    onChange={e => setUploadForm({ ...uploadForm, issn: e.target.value })}
                    placeholder="1234-5678"
                  />
                </div>
                <div className="jurnal__form-group">
                  <label>DOI</label>
                  <input
                    type="text"
                    value={uploadForm.doi}
                    onChange={e => setUploadForm({ ...uploadForm, doi: e.target.value })}
                    placeholder="10.1234/example"
                  />
                </div>
              </div>
              <div className="jurnal__form-row">
                <div className="jurnal__form-group jurnal__form-group--full">
                  <label>URL Sumber</label>
                  <input
                    type="url"
                    value={uploadForm.url_sumber}
                    onChange={e => setUploadForm({ ...uploadForm, url_sumber: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>
              <div className="jurnal__form-row">
                <div className="jurnal__form-group">
                  <label>Status</label>
                  <select
                    value={uploadForm.status}
                    onChange={e => setUploadForm({ ...uploadForm, status: e.target.value })}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
                <div className="jurnal__form-group">
                  <label>File PDF</label>
                  <div className="jurnal__file-input">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".pdf"
                      onChange={e => setUploadFile(e.target.files[0])}
                    />
                  </div>
                </div>
              </div>
              <div className="jurnal__modal-footer">
                <button type="button" className="jurnal__btn jurnal__btn--secondary" onClick={() => setShowUploadModal(false)}>
                  Batal
                </button>
                <button type="submit" className="jurnal__btn jurnal__btn--primary" disabled={uploading}>
                  {uploading ? 'Menyimpan...' : 'Simpan Jurnal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Claim Modal */}
      {showClaimModal && (
        <div className="jurnal__modal-overlay" onClick={() => setShowClaimModal(false)}>
          <div className="jurnal__modal jurnal__modal--large" onClick={e => e.stopPropagation()}>
            <div className="jurnal__modal-header">
              <h2><FiSearch /> Klaim Jurnal</h2>
              <button onClick={() => setShowClaimModal(false)}><FiX /></button>
            </div>
            <div className="jurnal__modal-body">
              <div className="jurnal__claim-info">
                <FiDatabase /> Mencari jurnal dengan nama: <strong>{user?.nama}</strong>
              </div>
              <div className="jurnal__claim-search">
                <select 
                  value={claimSumber}
                  onChange={e => { setClaimSumber(e.target.value); setClaimResults([]); }}
                  className="jurnal__claim-source"
                >
                  <option value="internal">📚 Database Perpustakaan</option>
                  <option value="crossref">🌐 CrossRef</option>
                  <option value="doaj">📖 DOAJ (Open Access)</option>
                  <option value="google_scholar">🔍 Google Scholar</option>
                  <option value="ebsco">📑 EBSCO</option>
                  <option value="scopus">🏆 Scopus</option>
                </select>
                <input
                  type="text"
                  value={claimSearchQuery}
                  onChange={e => setClaimSearchQuery(e.target.value)}
                  placeholder={claimSumber === 'internal' ? "Filter judul jurnal (opsional)..." : "Cari judul jurnal atau nama penulis..."}
                  onKeyDown={e => e.key === 'Enter' && handleClaimSearch()}
                />
                <button 
                  className="jurnal__btn jurnal__btn--primary"
                  onClick={handleClaimSearch}
                  disabled={claimSearching}
                >
                  {claimSearching ? 'Mencari...' : 'Cari'}
                </button>
              </div>

              <div className="jurnal__claim-results">
                {claimSearching ? (
                  <div className="jurnal__loading">
                    <div className="jurnal__loading-spinner"></div>
                    <p>Mencari jurnal...</p>
                  </div>
                ) : claimResults.length === 0 ? (
                  <div className="jurnal__claim-empty">
                    <FiSearch />
                    <p>{claimSumber === 'internal' 
                      ? `Tidak ditemukan jurnal dengan nama "${user?.nama}". Klik "Cari" untuk mencari ulang.` 
                      : 'Masukkan kata kunci untuk mencari jurnal'}
                    </p>
                  </div>
                ) : (
                  claimResults.map((jurnal, index) => (
                    <div key={jurnal.id_jurnal || index} className="jurnal__claim-item">
                      <div className="jurnal__claim-item-header">
                        <div className="jurnal__item-sumber">
                          {getSumberIcon(jurnal.sumber || jurnal.sumber_jurnal)}
                          <span>{getSumberLabel(jurnal.sumber || jurnal.sumber_jurnal || 'upload')}</span>
                        </div>
                        {jurnal.is_claimed || jurnal.id_anggota ? (
                          <span className="jurnal__claimed-badge"><FiCheck /> Sudah Diklaim</span>
                        ) : (
                          <button 
                            className="jurnal__btn jurnal__btn--primary jurnal__btn--small"
                            onClick={() => handleClaimJurnal(jurnal)}
                          >
                            <FiPlus /> Klaim
                          </button>
                        )}
                      </div>
                      <h4>{jurnal.judul}</h4>
                      <p className="jurnal__claim-authors">{jurnal.penulis}</p>
                      <div className="jurnal__claim-meta">
                        {jurnal.nama_jurnal && <span>{jurnal.nama_jurnal}</span>}
                        {jurnal.tahun_terbit && <span>{jurnal.tahun_terbit}</span>}
                      </div>
                      {jurnal.doi && <p className="jurnal__claim-doi">DOI: {jurnal.doi}</p>}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedJurnal && (
        <div className="jurnal__modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="jurnal__modal" onClick={e => e.stopPropagation()}>
            <div className="jurnal__modal-header">
              <h2><FiFileText /> Detail Jurnal</h2>
              <button onClick={() => setShowDetailModal(false)}><FiX /></button>
            </div>
            <div className="jurnal__modal-body jurnal__detail">
              <div className="jurnal__detail-header">
                <div className="jurnal__item-sumber">
                  {getSumberIcon(selectedJurnal.sumber)}
                  <span>{getSumberLabel(selectedJurnal.sumber)}</span>
                </div>
                {getStatusBadge(selectedJurnal.status)}
              </div>
              <h3>{selectedJurnal.judul}</h3>
              <p className="jurnal__detail-authors"><strong>Penulis:</strong> {selectedJurnal.penulis}</p>
              
              {selectedJurnal.abstrak && (
                <div className="jurnal__detail-section">
                  <strong>Abstrak:</strong>
                  <p>{selectedJurnal.abstrak}</p>
                </div>
              )}
              
              <div className="jurnal__detail-grid">
                <div><strong>Jurnal:</strong> {selectedJurnal.nama_jurnal || '-'}</div>
                <div><strong>Tahun:</strong> {selectedJurnal.tahun_terbit || '-'}</div>
                <div><strong>Volume:</strong> {selectedJurnal.volume || '-'}</div>
                <div><strong>Nomor:</strong> {selectedJurnal.nomor || '-'}</div>
                <div><strong>Halaman:</strong> {selectedJurnal.halaman || '-'}</div>
                <div><strong>ISSN:</strong> {selectedJurnal.issn || '-'}</div>
                <div className="jurnal__detail-full"><strong>DOI:</strong> {selectedJurnal.doi || '-'}</div>
              </div>
              
              <div className="jurnal__detail-actions">
                {selectedJurnal.url_sumber && (
                  <a 
                    href={selectedJurnal.url_sumber}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="jurnal__btn jurnal__btn--secondary"
                  >
                    <FiExternalLink /> Lihat Sumber
                  </a>
                )}
                {selectedJurnal.file_path && (
                  <a 
                    href={`/${selectedJurnal.file_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="jurnal__btn jurnal__btn--primary"
                  >
                    <FiDownload /> Download PDF
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jurnal;
